import { __rest, __assign } from 'tslib';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import React, { useCallback } from 'react';
import { useComboBoxState, useSearchFieldState } from 'react-stately';
export { Section as ComboBoxSection, Item } from 'react-stately';
import { useFilter, useComboBox, useSearchField } from 'react-aria';
import { MagnifySearchOutlineIcon, CrossSolidIcon, ChevronDownSolidIcon } from '@compono/ui-icons';
import { useTheme } from '../../hooks/useTheme.js';
import { Box } from '../../Box/index.js';
import { Loader } from '../../Loader/Loader.js';
import { Inline } from '../../Inline/Inline.js';
import { Popover } from '../../Popover/Popover.js';
import '../../Popover/PopoverImpl.js';
import { positionMatchWidth } from '../../Popover/usePopover.js';
import { Dropdown } from './Dropdown.js';
import { InputIconButton } from './InputIconButton.js';
import { InputIcon } from './InputIcon.js';

var ComboBox = function (_a) {
    var _b = _a.size, size = _b === void 0 ? "md" : _b, optional = _a.optional, error = _a.error, props = __rest(_a, ["size", "optional", "error"]);
    var contains = useFilter({ sensitivity: "base" }).contains;
    // add the minLength requirement for filter
    var defaultFilter = function (textValue, inputValue) {
        if (props.isAsync ||
            (inputValue !== "" && inputValue.length < props.minLength)) {
            return true;
        }
        else {
            return contains(textValue, inputValue);
        }
    };
    var state = useComboBoxState(__assign(__assign({}, props), { defaultFilter: defaultFilter, allowsEmptyCollection: Boolean(props.noMatchesFoundText) }));
    var chevronButtonRef = React.useRef(null);
    var inputRef = React.useRef(null);
    var listBoxRef = React.useRef(null);
    var popoverRef = React.useRef(null);
    var comboContainerRef = React.useRef(null);
    var clearButtonRef = React.useRef(null);
    var handleStyleProps = useTheme().handleStyleProps;
    var _c = useComboBox(__assign(__assign({}, props), { inputRef: inputRef, listBoxRef: listBoxRef, popoverRef: popoverRef }), state), inputProps = _c.inputProps, listBoxProps = _c.listBoxProps, 
    // labelProps,
    _d = _c.buttonProps; _d.onPress; _d.onPressStart; _d.onKeyDown; _d.onKeyUp; var chevronButtonProps = __rest(_d, ["onPress", "onPressStart", "onKeyDown", "onKeyUp"]);
    var portalStyles = handleStyleProps({
        themeKey: "typeaheadPortal",
    });
    var inputContainerStyles = handleStyleProps({
        error: error,
        size: size,
        themeKey: "typeaheadInputContainer",
        sx: __assign({}, (inputProps.disabled && {
            backgroundColor: "black.5",
        })),
    });
    // Get props for the clear button from useSearchField
    var searchProps = {
        label: props.label,
        value: state.inputValue,
        onChange: function (v) { return state.setInputValue(v); },
        "aria-label": "clear search",
    };
    var searchState = useSearchFieldState(searchProps);
    var _e = useSearchField(searchProps, searchState, inputRef).clearButtonProps; _e.onPress; _e.onPressStart; _e.onKeyDown; _e.onKeyUp; var clearButtonProps = __rest(_e, ["onPress", "onPressStart", "onKeyDown", "onKeyUp"]);
    var handleChevronClick = useCallback(function (e) {
        e.stopPropagation();
        state.toggle();
    }, [state]);
    var handleChevronKeyPress = useCallback(function (e) {
        var _a;
        if (["Enter", " "].includes(e.key)) {
            e.preventDefault();
            state.toggle();
            if (!state.isOpen) {
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
    }, [state.isOpen]);
    var handleClearOnKeyPress = function (e) {
        var _a;
        if (["Enter", " "].includes(e.key)) {
            e.preventDefault();
            searchState.setValue("");
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    var handleClearOnClick = function (e) {
        e.stopPropagation();
        searchState.setValue("");
    };
    return (jsxs(Box, __assign({ ref: comboContainerRef, themeKey: "typeaheadContainer" }, { children: [jsxs(Box, __assign({}, inputContainerStyles, { children: [jsxs(Box, __assign({ sx: { display: "flex", flex: 1, alignItems: "center" } }, { children: [jsx(InputIcon, { children: jsx(MagnifySearchOutlineIcon, {}) }), jsx(Box, __assign({ themeKey: "typeaheadInput", size: size, component: "input", ref: inputRef }, (!optional && { required: !optional }), inputProps))] })), jsxs(Inline, { children: [state.inputValue !== "" && (jsx(InputIconButton, __assign({}, clearButtonProps, { ref: clearButtonRef, title: "Clear search", disabled: Boolean(inputProps.disabled), onClick: handleClearOnClick, onKeyPress: handleClearOnKeyPress }, { children: jsx(InputIcon, { children: jsx(CrossSolidIcon, {}) }) }))), props.isLoading && (jsx(Inline, { children: jsx(Loader, { label: "Input Loading", hideLabel: true }) })), !props.isLoading && (jsx(InputIconButton, __assign({}, chevronButtonProps, { title: "Open Listbox", ref: chevronButtonRef, onClick: handleChevronClick, onKeyPress: handleChevronKeyPress, disabled: Boolean(inputProps.disabled) }, { children: jsx(InputIcon, { children: jsx(ChevronDownSolidIcon, {}) }) })))] })] })), jsx(Popover, __assign({ ref: popoverRef, hidden: !state.isOpen, targetRef: comboContainerRef, position: positionMatchWidth }, portalStyles, { children: jsx(Dropdown, __assign({}, listBoxProps, { listBoxRef: listBoxRef, state: state, noMatchesFoundText: props.noMatchesFoundText, size: size })) }))] })));
};
ComboBox.displayName = "ComboBox";

export { ComboBox };
//# sourceMappingURL=ComboBox.js.map
